import { useCallback, useEffect, useState } from 'react';
import { ArrowClockwise, Info } from 'shared/icons';
import { ExtractPaging } from '../../../utils/aggregation';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import ErrorCard from '../../ui/ErrorCard';
import { DateTimeText, Id, Mono, Sku } from '../../ui/Mono';
import Select from '../../ui/Select';
import { SkeletonTable } from '../../ui/Skeleton';
import StandardTable from '../../ui/StandardTable';
import { useToast } from '../../ui/Toast';
import { ExtractListRows } from '../payload';
import { BLING_SYNC_STATUSES, BlingSyncStatusVariant } from './shipmentModel';
import { ListBlingSyncQueue, RequeueBlingSync } from './shippingApi';

const DEFAULT_PAGE_SIZE = 30;
const PAGE_SIZE_OPTIONS = [15, 30, 50, 100];
const BLANK_CELL = <span className="text-mono-400">—</span>;

/**
 * FILA DO ESPELHO BLING — a tela que responde "por que o Bling não atualizou?".
 *
 * ⚠️ O ESPELHO É EM PEDAÇOS (dono, 13/08): cada depósito do melidash espelha no
 * depósito DELE no Bling. Por isso a fila tem uma linha por par **(produto,
 * depósito)** e esta tela mostra a coluna do DEPÓSITO: com 1‑para‑1, "o SKU X
 * falhou" não diz onde — o mesmo SKU pode ter uma linha por depósito, e a causa
 * (id do Bling errado, espelho desligado) costuma ser de UM deles.
 *
 * O fechamento do embarque enfileira, e o worker (`sync_bling_mirror_worker`)
 * envia o BALANÇO daquele depósito. A fila é marcador de sujeira, não retrato: a
 * quantidade é lida no instante do envio — por isso esta tela mostra ESTADO
 * (aguardando/enviado/falhou), não quantidade. Guardar um número aqui daria a
 * impressão de que existe um "valor a enviar" congelado, que é exatamente o que
 * o desenho do backend evita.
 *
 * O estado `unavailable` (404) continua aqui como rede de segurança de UI:
 * tabela vazia é a mentira mais perigosa possível nesta tela, porque "fila
 * limpa" é o que alguém concluiria antes de descobrir que o estoque do Bling
 * parou de andar.
 */
const BlingMirrorQueuePanel = ({ label }) => {
    const toast = useToast();
    const [rows, setRows] = useState([]);
    const [paging, setPaging] = useState({ total: 0, limit: DEFAULT_PAGE_SIZE, offset: 0 });
    const [status, setStatus] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [unavailable, setUnavailable] = useState(false);
    const [requeuing, setRequeuing] = useState(false);

    const load = useCallback(async () => {
        setLoadError(null);
        try {
            const offset = (Math.max(1, page) - 1) * Math.max(1, pageSize);
            const data = await ListBlingSyncQueue({ status: status || undefined, limit: pageSize, offset });
            const list = ExtractListRows(data, ['queue', 'bling_sync_queue']);
            setRows(list);
            setPaging(ExtractPaging(data, { total: list.length, limit: pageSize, offset }));
            setUnavailable(false);
        } catch (error) {
            // 404 = a rota não existe (o 404 do Express, não "fila vazia").
            // Qualquer outra falha é falha de verdade e vai para o ErrorCard.
            if (error?.status === 404) {
                setUnavailable(true);
                setRows([]);
            } else {
                setLoadError(error?.message || label('wms.common.load_error'));
                setRows([]);
            }
        } finally {
            setHasLoadedOnce(true);
        }
    }, [label, page, pageSize, status]);

    useEffect(() => {
        load();
    }, [load]);

    const handleRequeue = async () => {
        setRequeuing(true);
        try {
            await RequeueBlingSync({});
            toast.success(label('wms.bling_mirror.requeue_done'));
            await load();
        } catch (error) {
            toast.error(error?.message || label('wms.common.load_error'));
        } finally {
            setRequeuing(false);
        }
    };

    const columns = [
        {
            key: 'sku',
            label: label('wms.bling_mirror.field.product'),
            getValue: (row) => row.sku || row.product_id || '',
            render: (row) => (row.sku ? <Sku value={row.sku} /> : <Mono>{row.product_id}</Mono>),
        },
        {
            // QUAL PEDAÇO parou. Sem esta coluna, duas linhas do mesmo SKU
            // (dois depósitos) ficariam indistinguíveis na tela.
            key: 'deposit_code',
            label: label('wms.lpn.field.deposit'),
            getValue: (row) => row.deposit_code || row.deposit_id || '',
            render: (row) =>
                row.deposit_code ? <Id value={row.deposit_code} /> : row.deposit_id ? <Mono>{row.deposit_id}</Mono> : BLANK_CELL,
        },
        {
            key: 'status',
            label: label('wms.bling_mirror.field.status'),
            getValue: (row) => label(`wms.bling_sync_status.${row.status}`),
            render: (row) => (
                <Badge variant={BlingSyncStatusVariant(row.status)}>
                    {label(`wms.bling_sync_status.${row.status}`)}
                </Badge>
            ),
        },
        {
            key: 'retries',
            label: label('wms.bling_mirror.field.retries'),
            align: 'center',
            getValue: (row) => Number(row.retries ?? 0),
            render: (row) => <Mono>{Number(row.retries ?? 0)}</Mono>,
        },
        {
            key: 'next_attempt_at',
            label: label('wms.bling_mirror.field.next_attempt'),
            getValue: (row) => row.next_attempt_at || '',
            render: (row) => (row.next_attempt_at ? <DateTimeText value={row.next_attempt_at} relative /> : BLANK_CELL),
        },
        {
            key: 'sent_at',
            label: label('wms.bling_mirror.field.sent_at'),
            getValue: (row) => row.sent_at || '',
            render: (row) => (row.sent_at ? <DateTimeText value={row.sent_at} relative /> : BLANK_CELL),
        },
        {
            // O MOTIVO. É a coluna pela qual esta tela existe: sem ela, "falhou"
            // manda o Supervisor abrir o banco para descobrir se foi token,
            // cadastro do depósito ou produto sem id no Bling.
            key: 'last_error',
            label: label('wms.bling_mirror.field.last_error'),
            sortable: false,
            getValue: (row) => row.last_error || '',
            render: (row) =>
                row.last_error ? (
                    <span className="block max-w-md whitespace-pre-wrap break-words text-xs text-danger-text dark:text-danger-300">
                        {row.last_error}
                    </span>
                ) : (
                    BLANK_CELL
                ),
        },
        {
            key: 'created_at',
            label: label('wms.bling_mirror.field.created_at'),
            getValue: (row) => row.created_at || '',
            render: (row) => <DateTimeText value={row.created_at} relative />,
        },
    ];

    if (unavailable) {
        return (
            <div className="space-y-4">
                <div className="flex items-start gap-3 rounded-xl border-2 border-info-400 bg-info-soft px-4 py-4 dark:border-info-700 dark:bg-info-900/30">
                    <Info size={22} className="mt-0.5 shrink-0 text-info-text dark:text-info-300" />
                    <div className="space-y-1">
                        <p className="text-base font-bold text-info-text dark:text-info-200">
                            {label('wms.bling_mirror.unavailable_title')}
                        </p>
                        <p className="text-sm text-info-text dark:text-info-200">
                            {label('wms.bling_mirror.unavailable_body')}
                        </p>
                    </div>
                </div>
                <Button variant="secondary" onClick={load}>
                    <ArrowClockwise size={16} className="mr-2" />
                    {label('wms.common.retry')}
                </Button>
            </div>
        );
    }

    if (!hasLoadedOnce) {
        return <SkeletonTable columns={8} rows={6} />;
    }

    if (loadError) {
        return (
            <ErrorCard
                title={label('wms.bling_mirror.title')}
                message={loadError}
                action={label('wms.common.retry')}
                actionFn={load}
            />
        );
    }

    const hasFailed = rows.some((row) => row.status === 'FAILED');

    return (
        <div className="space-y-4">
            <p className="text-sm text-mono-600 dark:text-mono-400">{label('wms.bling_mirror.description')}</p>
            <StandardTable
                rows={rows}
                columns={columns}
                rowKey={(row, index) => row.id || index}
                countLabel={label('wms.bling_mirror.count')}
                exportFileName="wms-espelho-bling"
                emptyMessage={label('wms.bling_mirror.empty')}
                searchEnabled={false}
                defaultPageSize={DEFAULT_PAGE_SIZE}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                // Paginação de SERVIDOR: o `limit`/`offset` já vai na chamada, e
                // paginar de novo no cliente esvaziaria a página 2 (a tela
                // fatiaria de novo o que já veio fatiado).
                paginationMode="server"
                totalItems={paging.total}
                itemsPerPage={pageSize}
                onItemsPerPageChange={setPageSize}
                currentPage={page}
                onPageChange={setPage}
                toolbarLeft={
                    <Select
                        value={status}
                        onChange={(event) => {
                            setStatus(event.target.value);
                            setPage(1);
                        }}
                        className="w-48"
                    >
                        <option value="">{label('wms.bling_mirror.status_all')}</option>
                        {BLING_SYNC_STATUSES.map((option) => (
                            <option key={option} value={option}>
                                {label(`wms.bling_sync_status.${option}`)}
                            </option>
                        ))}
                    </Select>
                }
                toolbarRight={
                    // Só aparece quando existe falha: reenfileirar é a decisão
                    // humana de "arrumei o motivo, pode tentar de novo" — botão
                    // sem falha na tela seria um controle que não faz nada.
                    hasFailed ? (
                        <Button size="sm" variant="secondary" onClick={handleRequeue} loading={requeuing}>
                            <ArrowClockwise size={16} className="mr-2" />
                            {label('wms.bling_mirror.requeue')}
                        </Button>
                    ) : null
                }
            />
        </div>
    );
};

export default BlingMirrorQueuePanel;
